import { useState } from "react";
import { useParams } from "react-router-dom";
import HrmAttendanceTable from "../../components/hrm/HrmAttendanceTable";

import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  BorderlessTableOutlined,
  CalendarOutlined,
  CheckOutlined,
  ClockCircleOutlined,
  DownloadOutlined,
  EditOutlined,
} from "@ant-design/icons";
import {
  Button,
  Card,
  Col,
  DatePicker,
  Flex,
  Popconfirm,
  Row,
  Space,
  Statistic,
  Tooltip,
} from "antd";
import dayjs from "dayjs";
import {
  AttendanceSummaryDto,
  IAttendanceQuery,
  ISolveAttendanceQuery,
} from "../../models/Hrm";
import {
  useGetAttendanceByEmployee,
  useGetDataAttendance,
  useSolveAttendance,
} from "../../query/HrmQuery";
import { exportAttendanceToExcel } from "../../utils/exportAttendance";
import { notify } from "../../utils/toast";
import NotFound from "../NotFound/NotFound";
import { hrmService } from "../../services/hrmService";

const HrmAttendance = () => {
  const { alias } = useParams();
  const isValidAlias = Boolean(alias);
  const [solveResult, setSolveResult] = useState<AttendanceSummaryDto | null>(
    null,
  );
  const [date, setDate] = useState<ISolveAttendanceQuery | null>(null);
  const pullAttendanceMutation = useGetDataAttendance();
  const mutationSolveAttendance = useSolveAttendance();
  const [isExporting, setIsExporting] = useState(false);

  const [filter, setFilter] = useState<IAttendanceQuery>({
    page: 1,
    limit: 10,
  });

  const { data, isLoading } = useGetAttendanceByEmployee(
    alias!,
    filter,
    isValidAlias,
  );
  const getTimeSheetAndExport = async () => {
    try {
      if (!date?.startDate || !date?.endDate) {
        notify.warning("Vui lòng chọn khoảng thời gian để xuất file");
        return;
      }
      setIsExporting(true);
      const query = {
        startDate: dayjs(date.startDate).format("YYYY-MM-DD") as any,
        endDate: dayjs(date.endDate).format("YYYY-MM-DD") as any,
      };
      const result = await hrmService.getTimeSheetAttendance(query, alias!);
      if (result) {
        exportAttendanceToExcel(result, date);
      } else {
        notify.error("Không có dữ liệu để xuất.");
      }
    } catch (error) {
      notify.error("Lấy dữ liệu thất bại");
    } finally {
      setIsExporting(false);
    }
  };

  const onSubmitSolveAttendance = async () => {
    if (!date?.startDate || !date?.endDate) return;
    try {
      const res = await mutationSolveAttendance.mutateAsync({
        employeeId: alias!,
        query: {
          startDate: dayjs(date.startDate).format("YYYY-MM-DD") as any,
          endDate: dayjs(date.endDate).format("YYYY-MM-DD") as any,
        },
      });
      setSolveResult(res);
    } catch (err) {
      console.error(err);
    }
  };

  const onSubmitAttendance = async () => {
    if (!date?.startDate || !date?.endDate) {
      notify.warning("Vui lòng chọn khoảng thời gian");
      return;
    }
    try {
      await pullAttendanceMutation.mutateAsync({
        pull: {
          start: dayjs(date.startDate).format("YYYY-MM-DD") as any,
          end: dayjs(date.endDate).format("YYYY-MM-DD") as any,
          employeeId: alias!,
        },
        query: filter,
      });
      notify.success("Đồng bộ dữ liệu chấm công thành công");
    } catch (error) {
      notify.error("Đồng bộ dữ liệu thất bại");
    }
  };

  if (!alias || !isValidAlias) {
    return <NotFound />;
  }
  const statistics = [
    {
      title: "Đi muộn (phút)",
      value: solveResult?.lateMinutesTotal,
      color: "#ff0c20",
      icon: <ClockCircleOutlined />,
    },
    {
      title: "Về sớm (phút)",
      value: solveResult?.earlyMinutesTotal,
      color: "#fa8c16",
      icon: <ArrowDownOutlined />,
    },
    {
      title: "Tăng ca (giờ)",
      value: solveResult?.overtimeHoursTotal,
      color: "#52c41a",
      icon: <ArrowUpOutlined />,
    },
    {
      title: "Nghỉ phép",
      value: solveResult?.leaveRequestsTotal,
      color: "#16aaff",
      icon: <CalendarOutlined />,
    },
    {
      title: "Tổng ngày công",
      value: solveResult?.attendancesTotal,
      color: "#0246ff",
      icon: <BorderlessTableOutlined />,
    },
  ];

  const isDateSelected = !!date?.startDate && !!date?.endDate;

  return (
    <>
      <Flex justify="end" vertical className="mb-4">
        <Flex justify="end" gap="small" wrap="wrap">
          <Space wrap>
            <Space className="w-full">
              <DatePicker
                className="w-full"
                placeholder="Ngày bắt đầu"
                onChange={(date) =>
                  setDate((prev) => ({
                    ...prev!,
                    startDate: date?.toDate(),
                  }))
                }
                disabledDate={(current) =>
                  date?.endDate
                    ? current.isAfter(dayjs(date.endDate), "day")
                    : false
                }
              />
              <DatePicker
                className="w-full"
                placeholder="Ngày kết thúc"
                onChange={(date) =>
                  setDate((prev) => ({
                    ...prev!,
                    endDate: date?.toDate(),
                  }))
                }
                disabledDate={(current) =>
                  date?.startDate
                    ? current.isBefore(dayjs(date.startDate), "day")
                    : false
                }
              />
            </Space>

            <Tooltip
              title={
                !isDateSelected ? "Vui lòng chọn khoảng thời gian trước" : ""
              }
            >
              <Button
                icon={<DownloadOutlined />}
                onClick={getTimeSheetAndExport}
                loading={isExporting}
                type="primary"
                disabled={!isDateSelected}
                style={{ pointerEvents: !isDateSelected ? "none" : "auto" }}
              >
                Xuất Excel
              </Button>
            </Tooltip>
          </Space>
          <Tooltip
            title={
              !isDateSelected ? "Vui lòng chọn khoảng thời gian trước" : ""
            }
          >
            <span
              style={{
                display: "inline-block",
                cursor: !isDateSelected ? "not-allowed" : "pointer",
              }}
            >
              <Popconfirm
                title="Tính chấm công?"
                description="Xác nhận tính chấm công cho khoảng thời gian này?"
                onConfirm={onSubmitSolveAttendance}
                disabled={!isDateSelected}
                okText="Xác nhận"
                cancelText="Hủy"
              >
                <Button
                  type="primary"
                  icon={<CheckOutlined />}
                  disabled={!isDateSelected}
                  style={{ pointerEvents: !isDateSelected ? "none" : "auto" }}
                >
                  Tính chấm công
                </Button>
              </Popconfirm>
            </span>
          </Tooltip>
          <Tooltip
            title={
              !isDateSelected ? "Vui lòng chọn khoảng thời gian trước" : ""
            }
          >
            <span
              style={{
                display: "inline-block",
                cursor: !isDateSelected ? "not-allowed" : "pointer",
              }}
            >
              <Popconfirm
                title="Lấy dữ liệu từ máy chấm công?"
                description="Xác nhận đồng bộ dữ liệu chấm công?"
                onConfirm={onSubmitAttendance}
                disabled={!isDateSelected}
                okText="Đồng ý lấy"
                cancelText="Hủy không lấy nữa"
              >
                <Button
                  type="primary"
                  icon={<EditOutlined />}
                  loading={pullAttendanceMutation.isPending}
                  disabled={!isDateSelected}
                  style={{ pointerEvents: !isDateSelected ? "none" : "auto" }}
                >
                  Lấy dữ liệu từ máy chấm công
                </Button>
              </Popconfirm>
            </span>
          </Tooltip>
        </Flex>
        {solveResult && (
          <Card
            style={{ marginTop: 16, width: "100%" }}
            title={`Kết quả tính công ngày : ${dayjs(date?.startDate).format("DD/MM/YYYY")} - ${dayjs(
              date?.endDate,
            ).format("DD/MM/YYYY")}`}
          >
            <Row gutter={[8, 8]}>
              {statistics.map((item) => (
                <Col flex="1 0 20%" key={item.title}>
                  <Card>
                    <Statistic
                      title={item.title}
                      value={item.value}
                      valueStyle={{ color: item.color }}
                      prefix={item.icon}
                    />
                  </Card>
                </Col>
              ))}
            </Row>
          </Card>
        )}
      </Flex>
      <HrmAttendanceTable
        data={data}
        filter={filter}
        setFilters={setFilter}
        isLoading={isLoading}
      />
    </>
  );
};

export default HrmAttendance;
